import { useCallback, useEffect, useState } from 'react';
import {
  Abas,
  Aviso,
  Botao,
  Carregando,
  Cartao,
  paginar,
  Paginacao,
  Selo,
  Vazio,
} from '../../componentes/ui/index.ts';
import { api, dataHora, ErroApi } from '../../lib/api.ts';

type StatusEmail = 'pendente' | 'enviado' | 'erro';

type Email = {
  id: string;
  para: string;
  template: string;
  status: StatusEmail;
  agendadoPara: string;
  enviadoEm: string | null;
  tentativas: number;
  erro: string | null;
  inscricaoId: string | null;
  protocolo: string | null;
  nomeAdolescente: string | null;
};

type Resposta = {
  resumo: Record<StatusEmail, number>;
  emails: Email[];
};

const ROTULO_TEMPLATE: Record<string, string> = {
  confirmacao: 'Confirmação de inscrição',
  autorizacao: 'Pedido de autorização',
  lembrete_pagamento: 'Lembrete de pagamento',
  aviso_saude_faltando: 'Aviso de saúde faltando',
  lembrete_embarque: 'Lembrete de embarque',
  acesso_aprovado: 'Acesso aprovado',
  acesso_rejeitado: 'Acesso recusado',
};

const TOM: Record<StatusEmail, 'alerta' | 'sucesso' | 'erro'> = {
  pendente: 'alerta',
  enviado: 'sucesso',
  erro: 'erro',
};

const ROTULO_STATUS: Record<StatusEmail, string> = {
  pendente: 'na fila',
  enviado: 'enviado',
  erro: 'falhou',
};

/**
 * Fila e histórico de envio. É onde o admin percebe que o envio parou: e-mail
 * parado em "na fila" com horário no passado significa que o cron não está
 * rodando; "falhou" traz o motivo devolvido pelo provedor.
 */
export function FilaEmails() {
  const [aba, setAba] = useState<StatusEmail>('pendente');
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);

  // Trocar de aba volta para a primeira página.
  useEffect(() => setPagina(1), [aba]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await api<Resposta>(`/api/admin/emails?status=${aba}`));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao carregar os e-mails');
    } finally {
      setCarregando(false);
    }
  }, [aba]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const resumo = dados?.resumo;
  const visivel = paginar(dados?.emails ?? [], pagina);

  return (
    <Cartao
      titulo="Fila e histórico de e-mails"
      acao={
        <Botao variante="fantasma" tamanho="pequeno" onClick={() => void carregar()}>
          Atualizar
        </Botao>
      }
    >
      <div className="pilha">
        <p className="campo__dica">
          Os e-mails saem da fila em lotes, a cada minuto. Um e-mail “na fila” com horário já
          passado por muito tempo indica que o envio automático parou.
        </p>

        <Abas
          rotulo="Filtrar e-mails"
          valor={aba}
          aoMudar={setAba}
          opcoes={[
            { valor: 'pendente' as const, rotulo: 'Na fila', contagem: resumo?.pendente },
            { valor: 'enviado' as const, rotulo: 'Enviados', contagem: resumo?.enviado },
            { valor: 'erro' as const, rotulo: 'Com erro', contagem: resumo?.erro },
          ]}
        />

        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        {carregando && !dados ? (
          <Carregando />
        ) : !dados || dados.emails.length === 0 ? (
          <Vazio titulo={aba === 'pendente' ? 'Fila vazia' : 'Nada por aqui'}>
            {aba === 'pendente'
              ? 'Nenhum e-mail aguardando envio.'
              : 'Nenhum e-mail nesta situação.'}
          </Vazio>
        ) : (
          <div className="tabela-caixa">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Destinatário</th>
                  <th>E-mail</th>
                  <th>Situação</th>
                  <th>{aba === 'pendente' ? 'Agendado para' : 'Enviado em'}</th>
                  <th className="num">Tentativas</th>
                </tr>
              </thead>
              <tbody>
                {visivel.itens.map((e) => (
                  <tr key={e.id}>
                    <td>
                      {e.nomeAdolescente ?? '—'}
                      {e.protocolo && (
                        <div className="mono texto-fraco">{e.protocolo}</div>
                      )}
                    </td>
                    <td>
                      {ROTULO_TEMPLATE[e.template] ?? e.template}
                      <div className="texto-fraco">{e.para}</div>
                      {e.erro && (
                        <div className="texto-sm" style={{ color: 'var(--erro)' }}>
                          {e.erro}
                        </div>
                      )}
                    </td>
                    <td>
                      <Selo tom={TOM[e.status]} ponto>
                        {ROTULO_STATUS[e.status]}
                      </Selo>
                    </td>
                    <td className="mono texto-fraco">
                      {dataHora(e.status === 'pendente' ? e.agendadoPara : (e.enviadoEm ?? e.agendadoPara))}
                    </td>
                    <td className="num mono">{e.tentativas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginacao
          total={dados?.emails.length ?? 0}
          pagina={visivel.pagina}
          aoMudar={setPagina}
        />

        {dados && dados.emails.length >= 300 && (
          <p className="campo__dica">Mostrando os 300 mais recentes.</p>
        )}
      </div>
    </Cartao>
  );
}
